import { useQuery } from '@tanstack/react-query';
import { Activity, Car, ShieldAlert, UserPlus, Users } from 'lucide-react';
import { type ComponentType } from 'react';
import { Link } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { tempoDecorrido } from '@/lib/format';
import { supabase } from '@/lib/supabase';

interface KpiData {
  qtd_clientes_ativos: number;
  qtd_solicitacoes_abertas: number;
  qtd_cadastros_7d: number;
  qtd_cadastros_30d: number;
  qtd_veiculos_ativos: number;
}

async function carregarKpis(): Promise<KpiData> {
  const seteDiasAtras = new Date(Date.now() - 7 * 86_400_000).toISOString();
  const trintaDiasAtras = new Date(Date.now() - 30 * 86_400_000).toISOString();

  const [clientes, solicitacoes, cad7d, cad30d, veiculos] = await Promise.all([
    supabase
      .from('tab_clientes')
      .select('seq_cliente', { count: 'exact', head: true })
      .eq('ind_ativo', true),
    supabase
      .from('tab_solicitacoes_titular')
      .select('seq_solicitacao_titular', { count: 'exact', head: true })
      .eq('cod_status', 'ABERTA'),
    supabase
      .from('tab_clientes')
      .select('seq_cliente', { count: 'exact', head: true })
      .gte('dta_criacao', seteDiasAtras),
    supabase
      .from('tab_clientes')
      .select('seq_cliente', { count: 'exact', head: true })
      .gte('dta_criacao', trintaDiasAtras),
    supabase
      .from('tab_veiculos')
      .select('seq_veiculo', { count: 'exact', head: true })
      .eq('cod_status', 'ATIVO'),
  ]);

  return {
    qtd_clientes_ativos: clientes.count ?? 0,
    qtd_solicitacoes_abertas: solicitacoes.count ?? 0,
    qtd_cadastros_7d: cad7d.count ?? 0,
    qtd_cadastros_30d: cad30d.count ?? 0,
    qtd_veiculos_ativos: veiculos.count ?? 0,
  };
}

interface LogResumo {
  cod_evento: string;
  cod_entidade: string | null;
  dta_evento: string;
}

async function carregarLogsRecentes(): Promise<LogResumo[]> {
  const { data, error } = await supabase
    .from('tab_logs_auditoria')
    .select('cod_evento, cod_entidade, dta_evento')
    .order('dta_evento', { ascending: false })
    .limit(10);
  if (error) throw error;
  return (data ?? []) as LogResumo[];
}

function KpiCard({
  titulo,
  valor,
  icon: Icon,
  carregando,
}: {
  titulo: string;
  valor: number | undefined;
  icon: ComponentType<{ className?: string }>;
  carregando: boolean;
}) {
  return (
    <Card variant="elevated">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
          {titulo}
        </CardTitle>
        <div className="bg-octa-gradient shadow-octa-glow flex h-8 w-8 items-center justify-center rounded-lg">
          <Icon className="h-4 w-4 text-white" />
        </div>
      </CardHeader>
      <CardContent>
        {carregando ? (
          <Skeleton className="h-9 w-24" />
        ) : (
          <div className="text-octa-gradient text-3xl font-bold">{valor ?? 0}</div>
        )}
      </CardContent>
    </Card>
  );
}

export function DashboardPage() {
  const kpis = useQuery({ queryKey: ['kpis'], queryFn: carregarKpis });
  const logs = useQuery({ queryKey: ['logs-recentes'], queryFn: carregarLogsRecentes });

  return (
    <div className="space-y-6 p-6 lg:p-8">
      {/* Hero header */}
      <div className="bg-octa-hero relative -mx-6 -mt-6 overflow-hidden rounded-b-3xl border-b p-8 lg:-mx-8 lg:-mt-8">
        <div
          aria-hidden
          className="bg-octa-gradient pointer-events-none absolute -right-12 -top-12 h-64 w-64 rounded-full opacity-15 blur-3xl"
        />
        <div className="relative z-10">
          <h1 className="text-3xl font-bold tracking-tight">Inicio</h1>
          <p className="text-muted-foreground mt-1">Visao geral do seu Octa Pontos hoje.</p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <KpiCard
          titulo="Clientes ativos"
          valor={kpis.data?.qtd_clientes_ativos}
          icon={Users}
          carregando={kpis.isLoading}
        />
        <KpiCard
          titulo="Veículos ativos"
          valor={kpis.data?.qtd_veiculos_ativos}
          icon={Car}
          carregando={kpis.isLoading}
        />
        <KpiCard
          titulo="LGPD em aberto"
          valor={kpis.data?.qtd_solicitacoes_abertas}
          icon={ShieldAlert}
          carregando={kpis.isLoading}
        />
        <KpiCard
          titulo="Novos (7d)"
          valor={kpis.data?.qtd_cadastros_7d}
          icon={UserPlus}
          carregando={kpis.isLoading}
        />
        <KpiCard
          titulo="Novos (30d)"
          valor={kpis.data?.qtd_cadastros_30d}
          icon={Activity}
          carregando={kpis.isLoading}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Atalhos rapidos</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <Link
              to="/clientes"
              className="hover:bg-accent hover:text-accent-foreground rounded-md border p-3 text-sm transition-colors"
            >
              Buscar cliente
            </Link>
            <Link
              to="/lgpd/solicitacoes"
              className="hover:bg-accent hover:text-accent-foreground rounded-md border p-3 text-sm transition-colors"
            >
              Atender solicitacoes LGPD
            </Link>
            <Link
              to="/lgpd/logs"
              className="hover:bg-accent hover:text-accent-foreground rounded-md border p-3 text-sm transition-colors"
            >
              Ver logs de auditoria
            </Link>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Últimas atividades</CardTitle>
          </CardHeader>
          <CardContent>
            {logs.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : logs.data && logs.data.length > 0 ? (
              <ul className="divide-y">
                {logs.data.map((log, idx) => (
                  <li key={idx} className="flex items-center justify-between py-2 text-sm">
                    <div>
                      <span className="text-muted-foreground text-xs tracking-wider">
                        {log.cod_entidade ?? '-'}
                      </span>
                      <div className="font-medium">{log.cod_evento}</div>
                    </div>
                    <span className="text-muted-foreground text-xs">
                      {tempoDecorrido(log.dta_evento)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground py-4 text-center text-sm">
                Nenhuma atividade registrada ainda.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
